import { useEffect, useState } from 'react';
import { companyAPI } from '../../services/api';
import { setDisplayCurrency } from '../../utils/currency';

let cache = null;
let inflight = null;
const COMPANY_UPDATED_EVENT = 'ledgerpro_company_updated';

/**
 * Update the shared company cache in-place and notify every live consumer
 * (e.g. right after the user changes the base currency) so all open screens
 * re-render with the new values instead of waiting for a full page reload.
 */
export function updateCachedCompany(fields) {
  if (!cache) return;
  cache = { ...cache, ...fields };
  if (fields.base_currency) setDisplayCurrency(fields.base_currency);
  window.dispatchEvent(new CustomEvent(COMPANY_UPDATED_EVENT));
}

export function getCachedCompany() {
  return cache;
}

/**
 * Company profile (name, address, phone, logo, base_currency) with a
 * module-level cache so every printed document shares one fetch instead of
 * hitting the API each time. Reacts to updateCachedCompany() notifications.
 */
export function useCompany() {
  const [company, setCompany] = useState(cache);

  useEffect(() => {
    const onUpdate = () => setCompany({ ...cache });
    window.addEventListener(COMPANY_UPDATED_EVENT, onUpdate);
    return () => window.removeEventListener(COMPANY_UPDATED_EVENT, onUpdate);
  }, []);

  useEffect(() => {
    if (cache) return;
    if (!inflight) {
      inflight = companyAPI.current()
        .then((c) => { cache = c; return c; })
        .catch(() => null)
        .finally(() => { inflight = null; });
    }
    let mounted = true;
    inflight.then((c) => {
      if (c && c.base_currency) setDisplayCurrency(c.base_currency);
      if (mounted) setCompany(c);
    });
    return () => { mounted = false; };
  }, []);

  return company;
}
